// Simple client-side rate limiting (note: this should be supplemented with server-side limits)

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs?: number;
}

class RateLimiter {
  private attempts: Map<string, { count: number; firstAttempt: number; blockedUntil?: number }> = new Map();
  
  isBlocked(key: string, config: RateLimitConfig): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(key);
    
    if (!attempt) {
      return false;
    }
    
    // Check if currently blocked
    if (attempt.blockedUntil && now < attempt.blockedUntil) {
      return true;
    }
    
    // Reset if window has passed
    if (now - attempt.firstAttempt > config.windowMs) {
      this.attempts.delete(key);
      return false;
    }
    
    return false;
  }
  
  recordAttempt(key: string, config: RateLimitConfig): { allowed: boolean; remainingAttempts: number; resetTime?: number } {
    const now = Date.now();
    let attempt = this.attempts.get(key);
    
    // Check if blocked
    if (this.isBlocked(key, config)) {
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: attempt?.blockedUntil
      };
    }
    
    if (!attempt || now - attempt.firstAttempt > config.windowMs) {
      // First attempt or window reset
      attempt = {
        count: 1,
        firstAttempt: now
      };
    } else {
      // Increment existing attempt
      attempt.count++;
    }
    
    this.attempts.set(key, attempt);
    
    const remainingAttempts = Math.max(0, config.maxAttempts - attempt.count);
    
    if (attempt.count >= config.maxAttempts) {
      // Block user
      attempt.blockedUntil = now + (config.blockDurationMs || config.windowMs);
      this.attempts.set(key, attempt);
      
      return {
        allowed: false,
        remainingAttempts: 0,
        resetTime: attempt.blockedUntil
      };
    }
    
    return {
      allowed: true,
      remainingAttempts,
      resetTime: attempt.firstAttempt + config.windowMs
    };
  }
  
  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();

// Common rate limit configurations
export const RATE_LIMITS = {
  LOGIN: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 30 * 60 * 1000 // 30 minutes
  },
  PASSWORD_RESET: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    blockDurationMs: 60 * 60 * 1000 // 1 hour
  },
  PROMO_CODE: {
    maxAttempts: 10,
    windowMs: 5 * 60 * 1000, // 5 minutes
    blockDurationMs: 15 * 60 * 1000 // 15 minutes
  }
} as const;